// src/components/ListView.js - Card-style 2-column layout
import { useState, useEffect, useCallback, useMemo } from "react";
import { FiPlus, FiMinus, FiAlertTriangle, FiWifiOff, FiTrash2 } from "react-icons/fi";
import { getImageUrl } from "../utils/getImageUrl";
import { useContext } from "react";
import { AuthContext } from "../context/AuthContext";
import { useCart } from "../hooks/useCart";
import { useProductOrderStatsContext } from "../context/ProductOrderStatsContext";
import { useProductDiscountContext } from "../context/ProductDiscountContext";
import { useCustomerContext } from "../context/CustomerContext";
import discountService from "../services/discountService";
import apiClient from "../services/api";
import useAccess from "../hooks/useAccess";
import { mostPermissive } from "../permissions/access";

// Constants
const FALLBACK_IMAGE = "/static/images/no-image.png";

const ListView = ({ products = [], lastProductRef, onCartOperationStart, onCartOperationEnd, showStock = false }) => {
  const { isAuthenticated } = useContext(AuthContext);
  const { selectedCustomer } = useCustomerContext();
  const { can: canAccess, stateOf: accessStateOf } = useAccess();
  const can = (key) => !isAuthenticated || canAccess(key);
  const stateOf = (key) => (isAuthenticated ? accessStateOf(key) : mostPermissive(key));

  // Use new local-first cart system
  const {
    items: cartItems,
    addItem: handleAddToCart,
    updateItem: handleUpdateItem,
    removeItem: handleRemoveFromCart,
    getCartItem,
    getQuantityInCart
  } = useCart();

  // Use shared order statistics context
  const { getOrderStatsForProduct, ensureProductsFetched } = useProductOrderStatsContext();
  const { fetchDiscountsForProducts, getDiscountBadge, getDiscountBadgeInfo, getDiscountForProduct } = useProductDiscountContext();

  const productIds = useMemo(() => products.map(p => p.id).filter(id => id != null), [products]);

  const productIdsString = useMemo(() => productIds.join(','), [productIds]);

  // Cache for customer-specific prices
  const [customerPrices, setCustomerPrices] = useState({});

  // Fetch customer-specific prices when customer or products change
  useEffect(() => {
    const fetchCustomerPrices = async () => {
      if (!selectedCustomer?.id || productIds.length === 0) {
        setCustomerPrices({});
        return;
      }

      try {
        const response = await apiClient.post('/orders/get_customer_prices/', {
          product_ids: productIds,
          customer_id: selectedCustomer.id
        });
        setCustomerPrices(response.data || {});
      } catch (error) {
        console.error('[ListView] Error fetching customer prices:', error);
        setCustomerPrices({});
      }
    };

    fetchCustomerPrices();
  }, [selectedCustomer?.id, productIdsString]); // eslint-disable-line react-hooks/exhaustive-deps

  // Helper to get the effective price for a product (customer-specific or default)
  const getEffectivePrice = useCallback((product) => {
    if (selectedCustomer?.id && customerPrices[product.id?.toString()]) {
      return parseFloat(customerPrices[product.id.toString()].price);
    }
    return parseFloat(product.price || 0);
  }, [selectedCustomer?.id, customerPrices]);

  useEffect(() => {
    if (productIds.length > 0) {
      ensureProductsFetched(productIds);
      if (isAuthenticated) {
        fetchDiscountsForProducts(productIds, selectedCustomer?.id || null);
      }
    }
  }, [productIdsString, selectedCustomer?.id, ensureProductsFetched, fetchDiscountsForProducts, isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  // Local UI states
  const [selectedUnits, setSelectedUnits] = useState({});
  const [actionStates, setActionStates] = useState({});
  const [showDiscountTooltip, setShowDiscountTooltip] = useState(null);

  // Close tooltip when clicking outside
  useEffect(() => {
    if (showDiscountTooltip === null) return;

    const handleClickOutside = () => {
      setShowDiscountTooltip(null);
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showDiscountTooltip]);
  
  // Handle unit selection
  const handleUnitSelect = useCallback(async (productId, unitSymbol, product, unitObj) => {
    try {
      onCartOperationStart?.();
      
      // Get all cart items for this product
      const productCartItems = cartItems.filter(item => item.product?.id === productId);
      
      let itemWasConverted = false;
      
      // If there are existing cart items with different units, convert them
      if (productCartItems.length > 0) {
        const existingItem = productCartItems[0]; // Take the first (should only be one)
        
        if (existingItem.unit?.symbol !== unitSymbol) {
          // Keep the same quantity number, just change the unit
          const sameQuantity = parseFloat(existingItem.quantity || 0);
          
          // Remove old item and add new one with same quantity
          console.log(`[ListView] Removing item: ${existingItem.id} (${existingItem.quantity} ${existingItem.unit?.symbol})`);
          await handleRemoveFromCart(existingItem.id);
          
          console.log(`[ListView] Adding new item: ${sameQuantity} ${unitSymbol}`);
          const unitPrice = getEffectivePrice(product) * parseFloat(unitObj.conversion_factor || 1);
          await handleAddToCart(product, unitObj, sameQuantity, unitPrice);
          
          console.log(`[ListView] Changed unit from ${existingItem.quantity} ${existingItem.unit?.symbol} to ${sameQuantity} ${unitSymbol}`);
          itemWasConverted = true;
        }
      }
      
      // Update selected unit
      setSelectedUnits(prev => ({
        ...prev,
        [productId]: unitSymbol
      }));
      
      onCartOperationEnd?.();
      
      // Return whether an item was converted (added during conversion)
      return itemWasConverted;
    } catch (error) {
      console.error('Error switching unit:', error);
      onCartOperationEnd?.();
      return false;
    }
  }, [cartItems, handleRemoveFromCart, handleAddToCart, onCartOperationStart, onCartOperationEnd, getEffectivePrice]);

  // Handle quantity changes
  const handleQuantityChange = useCallback(async (cartItem, quantityChange, productId) => {
    try {
      onCartOperationStart?.();
      setActionStates(prev => ({ ...prev, [productId]: { loading: true } }));
      
      const newQuantity = parseFloat(cartItem.quantity || 0) + quantityChange;
      
      if (newQuantity <= 0) {
        await handleRemoveFromCart(cartItem.id);
      } else {
        await handleUpdateItem(cartItem.id, newQuantity);
      }
      
      setActionStates(prev => ({ ...prev, [productId]: { success: true } }));
      onCartOperationEnd?.();
      
      // Clear success state after short delay
      setTimeout(() => {
        setActionStates(prev => ({ ...prev, [productId]: {} }));
      }, 300);
    } catch (error) {
      console.error('Error changing quantity:', error);
      setActionStates(prev => ({ 
        ...prev, 
        [productId]: { error: true, errorMessage: error.message } 
      }));
      
      onCartOperationEnd?.();
      
      // Clear error state after delay
      setTimeout(() => {
        setActionStates(prev => ({ ...prev, [productId]: {} }));
      }, 2000);
    }
  }, [handleUpdateItem, handleRemoveFromCart, onCartOperationStart, onCartOperationEnd]);
  
  // Check online status
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Initialize selectedUnits based on products and cart
  const saveUnitSelections = useCallback((selections) => {
    try {
      localStorage.setItem('unit_selections', JSON.stringify(selections));
    } catch (error) {
      console.error('Error saving unit selections to localStorage:', error);
    }
  }, []);
  
  useEffect(() => {
    const initialUnits = {};
    
    // Try to load previous selections from localStorage
    let savedSelections = {};
    try {
      const saved = localStorage.getItem('unit_selections');
      if (saved) {
        savedSelections = JSON.parse(saved);
      }
    } catch (error) {
      console.error('Error loading saved unit selections:', error);
    }
    
    if (Array.isArray(products)) {
      products.forEach((product) => {
        const productId = product.id;
        
        // Priority 1: Check cart items first
        const productCartItems = cartItems.filter(
          item => item.product?.id === productId
        );
        
        if (productCartItems.length > 0) {
          // Use the unit from cart with highest quantity
          const sortedItems = [...productCartItems].sort(
            (a, b) => parseFloat(b.quantity || 0) - parseFloat(a.quantity || 0)
          );
          initialUnits[productId] = sortedItems[0].unit?.symbol || '';
        } 
        // Priority 2: Check localStorage for saved preference
        else if (savedSelections[productId]) {
          initialUnits[productId] = savedSelections[productId];
        }
        // Priority 3: Default logic
        else {
          if (product.hide_base_unit && product.extra_units && product.extra_units.length > 0) {
            // Extra units have nested unit object: { id, unit: { id, name, symbol }, conversion_factor }
            initialUnits[productId] = product.extra_units[0].unit?.symbol || product.extra_units[0].symbol;
          } else {
            initialUnits[productId] = product.unit?.symbol || '';
          }
        }
      });
      
      setSelectedUnits(initialUnits);
    }
  }, [products, cartItems]);
  
  // Save unit selections when changed
  useEffect(() => {
    if (Object.keys(selectedUnits).length > 0) {
      saveUnitSelections(selectedUnits);
    }
  }, [selectedUnits, saveUnitSelections]);

  
  // Handle image error
  const handleImageError = useCallback((e) => {
    e.target.onerror = null;
    e.target.src = getImageUrl(FALLBACK_IMAGE);
    e.target.classList.add("image-fallback");
  }, []);

  // Render
  if (!Array.isArray(products) || products.length === 0) {
    return <div className="text-center p-8 text-gray-500">No products to display.</div>;
  }

  const showImage = can('home.col.image', 'Product image');
  const showCategory = can('home.col.category', 'Category');
  const showProductBadges = can('home.col.product_badges', 'Product badges');
  const showOrderHistory = can('home.section.order_history', 'Order history (usual / last order)');
  const showName = can('home.col.name', 'Product name');
  const showSku = can('home.col.sku', 'SKU');
  const showStockCol = can('home.col.stock', 'Stock');
  const showPrice = can('home.col.price', 'Price');
  const showDiscount = can('home.badge.discount', 'Discount badge');
  const showLicenceNotice = can('home.badge.licence_required', 'Premises licence notice');
  const showCartQuantity = can('home.badge.cart_quantity', 'Quantity in cart');
  const unitState = stateOf('home.field.unit', 'Unit selector');
  const quantityState = stateOf('home.field.quantity', 'Quantity');
  const addState = stateOf('home.btn.add_to_cart', 'Add to cart');
  const removeState = stateOf('home.btn.remove_from_cart', 'Remove from cart');

  return (
    <div className="product-grid gap-4 p-2">
      {products.map((product, index) => {
        // Skip invalid products
        if (!product || !product.id || !product.unit) {
          return null;
        }

        const productId = product.id;
        const selectedUnitSymbol = selectedUnits[productId] || product.unit.symbol;
        const actionState = actionStates[productId] || { loading: false, success: false, error: false };

        // Prepare unit data - normalize extra_units which have nested structure: { id, unit: {...}, conversion_factor }
        const normalizedExtraUnits = (product.extra_units || []).map(eu => ({
          id: eu.unit?.id || eu.id,
          name: eu.unit?.name || eu.name,
          symbol: eu.unit?.symbol || eu.symbol,
          conversion_factor: eu.conversion_factor
        }));
        const availableUnits = (product.hide_base_unit
          ? normalizedExtraUnits
          : [{ ...product.unit, conversion_factor: 1 }, ...normalizedExtraUnits]
        ).filter(u => u && u.symbol);

        const selectedUnitObj = availableUnits.find(unit => unit.symbol === selectedUnitSymbol) || availableUnits[0];
        // Get cart data - find any cart item for this product (should only be one unit now)
        const productCartItems = cartItems.filter(item => item.product?.id === productId);
        const selectedCartItem = productCartItems.length > 0 ? productCartItems[0] : null;
        const selectedQuantity = selectedCartItem ? parseFloat(selectedCartItem.quantity || 0) : 0;
        const primaryImage = product.images?.find(img => img.is_primary)?.image || product.images?.[0]?.image || FALLBACK_IMAGE;

        // Get discount info for this product
        const discountBadge = getDiscountBadge(productId);
        const discountBadgeInfo = getDiscountBadgeInfo(productId);
        const discountBadgeText = discountBadgeInfo?.badgeConfig?.customText
          || (discountBadgeInfo?.type === 'fixed_price' ? null : discountBadge);
        const hasAppliedDiscount = selectedCartItem?.discount_percent > 0 && selectedCartItem?.discounted_price;
        const effectivePrice = getEffectivePrice(product);
        const appliedDiscount = hasAppliedDiscount ? {
          original_price: parseFloat(selectedCartItem.price_at_add || effectivePrice || 0),
          final_price: parseFloat(selectedCartItem.discounted_price || selectedCartItem.price_at_add || effectivePrice || 0),
          discount_percent: parseFloat(selectedCartItem.discount_percent || 0)
        } : null;

        return (
          <div
            key={productId}
            className={`relative bg-white rounded-lg shadow-md hover:shadow-lg transition-all duration-200 border border-gray-200 overflow-visible sm:min-w-[600px] w-full min-w-0 flex flex-col ${actionState.loading ? 'opacity-75' : ''}`}
            ref={index === products.length - 1 ? lastProductRef : null}
          >

            {/* Three Column Layout: Image Left, Content Center, Calculator Right */}
            <div className="flex flex-1 min-w-0">
              {/* Left Side - Image (smaller by another 10px) */}
              <div className="relative w-32 h-32 sm:w-40 sm:h-40 flex-shrink-0 bg-gray-50 rounded-tl-lg sm:rounded-none">
                {/* Category Badge Overlay */}
                {showCategory && (
                <div className="absolute top-1 left-1 right-1 z-10">
                  <span className="inline-block max-w-full truncate align-top bg-gray-800/90 backdrop-blur-sm text-white text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded shadow-sm">
                    {product.category?.name || "Uncategorized"}
                  </span>
                </div>
                )}

                {/* Status Indicators */}
                <div className="absolute top-1 right-1 z-10 flex gap-0.5">
                  {actionState.error && (
                    <span className="inline-flex items-center justify-center p-1 rounded-full bg-red-500 text-white shadow-sm" title={actionState.errorMessage}>
                      <FiAlertTriangle className="w-2 h-2" />
                    </span>
                  )}
                  {!isOnline && (
                    <span className="inline-flex items-center justify-center p-1 rounded-full bg-yellow-500 text-gray-900 shadow-sm">
                      <FiWifiOff className="w-2 h-2" />
                    </span>
                  )}
                </div>

                {/* SKU Badge Overlay - Bottom Center */}
                <div className="absolute bottom-1 left-0 right-0 z-10 flex justify-center gap-1">
                  {showSku && (
                  <span className="inline-block bg-gray-800/90 backdrop-blur-sm text-white text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded shadow-sm">
                    <span className="text-gray-300">SKU:</span> <span className="font-mono">{product.sku || "******"}</span>
                  </span>
                  )}
                  {showStock && showStockCol && (
                    <span className="inline-block bg-blue-800/90 backdrop-blur-sm text-white text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded shadow-sm">
                      <span className="text-blue-200">Stock:</span> {parseFloat(product.stock || 0)} {product.unit?.symbol || ''}
                      {parseFloat(product.reserved || 0) > 0 && (
                        <span className="text-amber-300"> (-{parseFloat(product.reserved)})</span>
                      )}
                    </span>
                  )}
                </div>

                {/* Product Image */}
                {showImage && (
                <img
                  src={getImageUrl(primaryImage)}
                  className="h-full w-full object-contain transition-transform duration-200 hover:scale-105 p-1.5 sm:p-2"
                  alt={product.name}
                  onError={handleImageError}
                />
                )}
              </div>

              {/* Center - Content (2/5 width) */}
              <div className="relative flex-1 min-w-0 p-2 sm:p-3 flex flex-col">
                {/* Product Badges - Top Right */}
                {showProductBadges && product.badges && product.badges.length > 0 && (
                  <div className="sm:absolute sm:top-1 sm:right-1 z-10 flex flex-row flex-wrap items-start gap-1 mb-1 sm:mb-0">
                    {product.badges.filter(b => b.is_active).slice(0, 3).map((badge) => (
                      <span
                        key={badge.id}
                        className="text-[9px] px-1.5 py-0.5 rounded shadow-sm font-medium whitespace-nowrap"
                        style={{ backgroundColor: badge.color, color: badge.text_color }}
                        title={badge.name}
                      >
                        {badge.name}
                      </span>
                    ))}
                  </div>
                )}

                {/* Product Name */}
                {showName && (
                <h3 className={`font-semibold text-gray-900 text-[13px] leading-snug sm:text-sm mb-2 line-clamp-3 sm:line-clamp-2 break-words ${showProductBadges && product.badges?.some(b => b.is_active) ? 'sm:pr-16' : ''}`} title={product.name}>
                  {product.name || "Unnamed Product"}
                </h3>
                )}

                {/* Unit Buttons Row */}
                <div className="mb-1 flex flex-wrap justify-end items-center gap-2">
                  {/* Fixed Price Discount - show old price crossed out + new price */}
                  {showPrice && discountBadgeInfo?.type === 'fixed_price' && discountBadgeInfo.originalPrice && !appliedDiscount && isAuthenticated && (
                    <div className="flex items-center gap-1">
                      <span className="text-gray-400 line-through text-xs">
                        £{discountBadgeInfo.originalPrice.toFixed(2)}
                      </span>
                      <span className="text-green-600 font-bold text-sm">
                        £{discountBadgeInfo.newPrice.toFixed(2)}
                      </span>
                      {selectedCartItem && discountBadgeInfo.newPrice < discountBadgeInfo.originalPrice && (
                        <span className="text-green-600 text-xs">
                          (-{Math.round((1 - discountBadgeInfo.newPrice / discountBadgeInfo.originalPrice) * 100)}%)
                        </span>
                      )}
                    </div>
                  )}
                  {/* Discount Badge - shown before unit buttons until the product is added to the cart */}
                  {showDiscount && discountBadgeText && !selectedCartItem && isAuthenticated && (
                    <span className="relative inline-block">
                      <button
                        type="button"
                        className="text-xs px-2 py-1 rounded font-bold whitespace-nowrap cursor-pointer"
                        style={{
                          backgroundColor: discountBadgeInfo?.badgeConfig?.color || '#DC2626',
                          color: discountBadgeInfo?.badgeConfig?.textColor || '#FFFFFF',
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowDiscountTooltip(showDiscountTooltip === productId ? null : productId);
                        }}
                      >
                        {discountBadgeText}
                      </button>
                      {showDiscountTooltip === productId && (() => {
                        const discount = getDiscountForProduct(productId);
                        const customDescription = discountBadgeInfo?.badgeConfig?.customDescription;
                        const explanation = customDescription || (discount ? discountService.formatDiscountConditionExplanation(discount) : null);
                        if (!explanation) return null;
                        return (
                          <div className="absolute z-50 bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64">
                            <div className="bg-gray-900 text-white text-xs rounded-lg py-2 px-3 shadow-lg">
                              <div className="font-medium mb-1">{discount?.name}</div>
                              <div>{explanation}</div>
                              <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 rotate-45 w-2 h-2 bg-gray-900"></div>
                            </div>
                          </div>
                        );
                      })()}
                    </span>
                  )}
                  {showLicenceNotice && isAuthenticated && product.alcohol_blocked && (
                    <div className="text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                      Premises licence required
                    </div>
                  )}
                  {/* Unit Buttons */}
                  {unitState !== 'hidden' && isAuthenticated && selectedUnitObj && !product.alcohol_blocked && (
                    <div className="flex flex-wrap justify-end gap-x-2 gap-y-2 sm:gap-1 overflow-visible relative">
                      {availableUnits.map((unitObj) => {
                        if (!unitObj || !unitObj.symbol || !unitObj.id) return null;
                        const unitQuantity = getQuantityInCart(productId, unitObj.symbol);
                        const unitPrice = (getEffectivePrice(product) * parseFloat(unitObj.conversion_factor || 1));
                        const isSelected = selectedUnitSymbol === unitObj.symbol && !actionState.loading;
                        const cf = parseFloat(unitObj.conversion_factor || 1);
                        const unitDisplay = cf === 1
                          ? unitObj.symbol
                          : `1 ${unitObj.symbol} (${[cf, product.unit?.symbol].filter(Boolean).join(" ")})`;
                        
                        return (
                          <div key={`${productId}-${unitObj.id}`} className="relative overflow-visible">
                            <button
                              type="button"
                              className={`relative px-1 py-1 rounded-md border transition-all duration-200 text-xs flex-shrink-0 min-w-[76px] sm:min-w-[70px] min-h-[44px] sm:min-h-0 ${
                                isSelected
                                  ? "border-blue-500 bg-blue-50 text-blue-900" 
                                  : "border-gray-300 bg-white hover:border-blue-400 hover:bg-blue-25"
                              }`}
                              onClick={async () => {
                                // If clicking already selected unit, add +1
                                if (selectedUnitSymbol === unitObj.symbol) {
                                  const cartItem = getCartItem(productId, unitObj.symbol);
                                  if (cartItem) {
                                    handleQuantityChange(cartItem, 1, productId);
                                  } else {
                                    onCartOperationStart?.();
                                    try {
                                      await handleAddToCart(product, unitObj, 1, unitPrice);
                                      onCartOperationEnd?.();
                                    } catch (error) {
                                      onCartOperationEnd?.();
                                      throw error;
                                    }
                                  }
                                } else {
                                  // Clicking different unit - switch unit (this will convert existing items)
                                  const itemWasConverted = await handleUnitSelect(productId, unitObj.symbol, product, unitObj);
                                  
                                  // If no items were converted (no existing items), add 1
                                  if (!itemWasConverted) {
                                    onCartOperationStart?.();
                                    try {
                                      await handleAddToCart(product, unitObj, 1, unitPrice);
                                      onCartOperationEnd?.();
                                    } catch (error) {
                                      onCartOperationEnd?.();
                                      throw error;
                                    }
                                  }
                                }
                              }}
                              disabled={actionState.loading || unitState === 'readonly' || addState !== 'visible' || (selectedUnitSymbol === unitObj.symbol && unitQuantity > 0 && quantityState !== 'editable')}
                            >
                              <div className="h-full flex flex-col justify-between p-1">
                                <div className="text-left font-semibold text-xs">
                                  {unitDisplay}
                                </div>
                                {showPrice && (
                                <div className="text-right font-bold text-xs">
                                  {appliedDiscount && isSelected ? (
                                    <>
                                      <span className="text-gray-400 line-through text-[10px] mr-0.5">
                                        £{appliedDiscount.original_price.toFixed(2)}
                                      </span>
                                      <span className="text-green-600">
                                        £{appliedDiscount.final_price.toFixed(2)}
                                      </span>
                                    </>
                                  ) : (
                                    `£${unitPrice.toFixed(2)}`
                                  )}
                                </div>
                                )}
                              </div>
                            </button>

                            {showCartQuantity && unitQuantity > 0 && (
                              <span className="absolute bg-yellow-500 text-black rounded-full h-6 w-6 flex items-center justify-center text-sm font-bold shadow-lg border-2 border-white" style={{zIndex: 9999, top: '-8px', right: '-8px'}}>
                                {unitQuantity}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                
                {/* Spacer to push cart controls and banners to bottom */}
                <div className="flex-grow"></div>
                
                {/* Cart Controls */}
                {isAuthenticated && selectedQuantity > 0 && (removeState !== 'hidden' || quantityState !== 'hidden') && (
                  <div className="bg-white border border-gray-200 rounded-lg p-1.5 sm:p-2 mb-1 mt-1">
                    {/* Quantity Controls */}
                    <div className="flex items-center justify-between gap-1">
                      {/* Delete Button - 15% */}
                      {removeState !== 'hidden' && (
                      <button
                        className="border border-red-600 text-red-600 hover:bg-red-50 p-1 flex items-center justify-center w-[20%] h-[38px] sm:h-[32px] transition-all duration-200 rounded"
                        onClick={async () => {
                          onCartOperationStart?.();
                          try {
                            await handleRemoveFromCart(selectedCartItem.id);
                            onCartOperationEnd?.();
                          } catch (error) {
                            onCartOperationEnd?.();
                            throw error;
                          }
                        }}
                        disabled={actionState.loading || removeState === 'disabled'}
                        title="Remove from cart"
                      >
                        <FiTrash2 />
                      </button>
                      )}
                      
                      {quantityState !== 'hidden' && (<>
                      {/* Decrease Button - 20% */}
                      <button
                        className="bg-red-600 text-white hover:bg-red-700 flex items-center justify-center w-[20%] h-[38px] sm:h-[32px] transition-all duration-200 rounded"
                        onClick={() => handleQuantityChange(selectedCartItem, -1, productId)}
                        disabled={actionState.loading || quantityState === 'readonly'}
                        title="Decrease quantity"
                      >
                        <FiMinus />
                      </button>
                      
                      
                      {/* Increase Button - 20% */}
                      <button
                        className="bg-green-600 text-white hover:bg-green-700 flex items-center justify-center w-[20%] h-[38px] sm:h-[32px] transition-all duration-200 rounded"
                        onClick={() => handleQuantityChange(selectedCartItem, 1, productId)}
                        disabled={actionState.loading || quantityState === 'readonly'}
                        title="Increase quantity"
                      >
                        <FiPlus />
                      </button>

                      {/* Increase by 5 - 20% */}
                      <button
                        className="bg-green-600 text-white hover:bg-green-700 flex items-center justify-center w-[20%] h-[38px] sm:h-[32px] transition-all duration-200 rounded"
                        onClick={() => handleQuantityChange(selectedCartItem, 5, productId)}
                        disabled={actionState.loading || quantityState === 'readonly'}
                        title="Add 5"
                      >
                        <FiPlus />5
                      </button>
                      
                      {/* Increase by 10 - 15% */}
                      <button
                        className="bg-green-600 text-white hover:bg-green-700 flex items-center justify-center w-[20%] h-[38px] sm:h-[32px] transition-all duration-200 rounded"
                        onClick={() => handleQuantityChange(selectedCartItem, 10, productId)}
                        disabled={actionState.loading || quantityState === 'readonly'}
                        title="Add 10"
                      >
                        <FiPlus />10
                      </button>
                      </>)}
                      
                    </div>
                  </div>
                )}
                
              </div>

            </div>

            {/* Bottom Order History Banners - Full Width */}
            <div className="mt-auto">
              {showOrderHistory && (() => {
                const orderStats = getOrderStatsForProduct(productId);
                const hasOrderHistory = orderStats && orderStats.total_orders > 0;

                if (!hasOrderHistory) {
                  return null;
                }

                return (
                  <div className="flex w-full">
                    {/* Usual Order Banner - Half Width */}
                    <div className="flex-1 min-w-0 px-2 py-1.5 sm:px-3 sm:py-2 border-t bg-blue-50 border-blue-200 rounded-bl-lg">
                      <div className="text-center">
                        <div className="text-[11px] sm:text-xs font-semibold text-blue-700">
                          {`USUAL: ${orderStats.usual_quantity || 0} ${orderStats.usual_unit || ''} ${orderStats.usual_frequency ? `Every ${orderStats.usual_frequency} days` : ''}`.trim()}
                        </div>
                      </div>
                    </div>

                    {/* Last Order Banner - Half Width */}
                    <div className="flex-1 min-w-0 px-2 py-1.5 sm:px-3 sm:py-2 border-t border-l bg-green-50 border-green-200 rounded-br-lg">
                      <div className="text-center">
                        <div className="text-[11px] sm:text-xs font-semibold text-green-700">
                          {`LAST: ${orderStats.last_quantity || 0} ${orderStats.last_unit || ''} ${orderStats.last_order_date ? new Date(orderStats.last_order_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : ''}`.trim()}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>

          </div>
        );
      })}
    </div>
  );
};

export default ListView;